import React, { useState, useEffect } from 'react';
import { Package, Clock, CheckCircle, AlertCircle, ExternalLink, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const OrderProgress = ({ status, t }) => {
  const stepIndex = { pending: 0, in_progress: 1, completed: 2 }[status];
  if (stepIndex === undefined) return null;
  const steps = [t('myOrders.progressPaid'), t('myOrders.inProgress'), t('myOrders.completed')];

  return (
    <div className="flex items-center w-full px-1" data-testid="order-progress">
      {steps.map((label, i) => {
        const done = i === 0 || i < stepIndex || status === 'completed';
        const current = i === stepIndex && i !== 0 && status !== 'completed';
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <div className={`flex-1 h-0.5 mx-1.5 rounded-full transition-colors ${i <= stepIndex ? 'bg-cyan-500' : 'bg-zinc-700'}`} />
            )}
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center border-2 transition-all ${
                done ? 'bg-cyan-500 border-cyan-500' : current ? 'border-cyan-400 bg-cyan-500/20 animate-pulse' : 'border-zinc-700 bg-zinc-800'
              }`}>
                {done ? (
                  <CheckCircle className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" />
                ) : (
                  <span className={`w-1.5 h-1.5 rounded-full ${current ? 'bg-cyan-400' : 'bg-zinc-600'}`} />
                )}
              </div>
              <span className={`text-[9px] md:text-[10px] font-medium whitespace-nowrap ${
                done || current ? 'text-cyan-400' : 'text-gray-600'
              }`}>{label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const ADDON_META = {
  preparation: { label: 'Préparation', desc: 'On s\'occupe de tout à votre place', icon: '🧰' },
  prioritaire: { label: 'Prioritaire', desc: 'Ta commande passe en priorité', icon: '⚡' },
};

const AddOnSection = ({ order, API_URL, token }) => {
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const svc = (order.service_type || '').toLowerCase();
    const relevant = svc.includes('windows') || svc.includes('pack');
    if (!relevant) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders/${order.id}/addon-options`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setAvailable(data.available || []);
        }
      } catch (e) { /* silencieux */ }
    })();
    return () => { cancelled = true; };
  }, [order.id, order.service_type, API_URL, token]);

  if (!available.length) return null;

  const toggle = (key) => {
    setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };
  const total = available.filter((a) => selected.includes(a.key)).reduce((s, a) => s + a.price, 0);

  const payByCard = async () => {
    if (!selected.length) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/addon-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ options: selected }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        setSubmitting(false);
      }
    } catch (e) {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-2 border-t border-cyan-900/30 pt-3" data-testid={`addon-section-${order.id.slice(0, 8)}`}>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid={`addon-open-${order.id.slice(0, 8)}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/15 text-cyan-300 text-[11px] md:text-xs rounded-lg hover:bg-cyan-500/25 transition-colors font-semibold"
        >
          <span className="text-sm">➕</span> Ajouter une option
        </button>
      ) : (
        <div className="space-y-3">
          <p className="text-gray-300 text-[11px] md:text-xs font-semibold">Ajouter une option à ta commande :</p>
          <div className="flex flex-wrap gap-2">
            {available.map((a) => {
              const active = selected.includes(a.key);
              const meta = ADDON_META[a.key] || { label: a.label, desc: '', icon: '➕' };
              return (
                <button
                  key={a.key}
                  onClick={() => toggle(a.key)}
                  data-testid={`addon-toggle-${a.key}-${order.id.slice(0, 8)}`}
                  className={`text-left px-3 py-2 rounded-lg border transition-all ${
                    active
                      ? 'border-cyan-500 bg-cyan-500/15'
                      : 'border-zinc-700 bg-zinc-800/50 hover:border-cyan-700'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span>{meta.icon}</span>
                    <span className="text-white text-xs font-bold">{meta.label}</span>
                    <span className="text-cyan-400 text-xs font-bold">+{a.price}€</span>
                  </div>
                  <div className="text-gray-500 text-[10px] mt-0.5">{meta.desc}</div>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <span className="text-white text-xs font-bold">
              Total : <span className="text-cyan-400">{total.toFixed(2)}€</span>
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => { setOpen(false); setSelected([]); }}
                className="px-3 py-1.5 text-gray-400 text-[11px] md:text-xs rounded-lg hover:text-white transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={payByCard}
                disabled={!selected.length || submitting}
                data-testid={`addon-pay-card-${order.id.slice(0, 8)}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500 text-white text-[11px] md:text-xs rounded-lg hover:bg-cyan-400 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <span>💳</span>}
                Payer par carte
              </button>
            </div>
          </div>
          <p className="text-gray-500 text-[10px] leading-relaxed">
            💡 Pour <span className="text-gray-400">PayPal, Paysafecard ou Crypto</span>, utilise le bouton
            {' '}<span className="text-cyan-400">« Ajouter une option »</span> directement dans ton ticket Discord.
          </p>
        </div>
      )}
    </div>
  );
};

const MesCommandesPage = () => {
  const { isAuthenticated, token, loginWithDiscord } = useAuth();
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const navigate = useNavigate();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await fetch(`${API_URL}/api/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setOrders(data);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, token, API_URL]);

  const getServiceName = (type) => {
    const names = {
      'windows': 'Service Windows',
      'app-tools-trial': 'App-Tools Trial',
      'app-tools-premium': 'App-Tools Premium',
      'app-tools-lifetime': 'App-Tools Lifetime',
      'bios': 'Service BIOS'
    };
    return names[type] || type;
  };

  const getServicePrice = (type) => {
    const prices = {
      'windows': '5€',
      'app-tools-trial': 'Gratuit',
      'app-tools-premium': '5€',
      'app-tools-lifetime': '10€',
      'bios': '3€'
    };
    return prices[type] || '-';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
            <Clock className="w-3 h-3" />
            {t('myOrders.pending')}
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
            <AlertCircle className="w-3 h-3" />
            {t('myOrders.inProgress')}
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
            <CheckCircle className="w-3 h-3" />
            {t('myOrders.completed')}
          </span>
        );
      default:
        return null;
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    setDownloadingId(orderId);
    try {
      const response = await fetch(`${API_URL}/api/orders/${orderId}/invoice`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(t('myOrders.downloadError'));
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture-${orderId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert(t('myOrders.downloadError'));
    } finally {
      setDownloadingId(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen py-10 md:py-16 px-4 md:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <Package className="w-12 h-12 md:w-16 md:h-16 text-cyan-500 mx-auto mb-4 md:mb-6" />
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4">{t('myOrders.title')} {t('myOrders.titleHighlight')}</h1>
          <p className="text-gray-400 mb-6 md:mb-8 text-sm md:text-base px-4">
            {t('myOrders.loginPrompt')}
          </p>
          <Button
            onClick={loginWithDiscord}
            className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 md:px-8 py-2 md:py-3 text-sm md:text-base"
          >
            {t('myOrders.loginButton')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-16 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        {new URLSearchParams(window.location.search).get('addon') === 'success' && (
          <div className="mb-6 p-3 md:p-4 rounded-xl bg-green-500/15 border border-green-600/40 text-green-300 text-xs md:text-sm flex items-center gap-2" data-testid="addon-success-banner">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            Option(s) ajoutée(s) avec succès ! Notre équipe en tient compte pour ta prestation. 💙
          </div>
        )}
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-cyan-950/50 border border-cyan-800/50 text-cyan-400 text-[10px] md:text-xs font-semibold uppercase tracking-wider mb-3 md:mb-4">
            {t('myOrders.badge')}
          </span>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4">
            {t('myOrders.title')} <span className="text-cyan-500">{t('myOrders.titleHighlight')}</span>
          </h1>
          <p className="text-gray-400 text-xs md:text-base">
            {t('myOrders.subtitle')}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 md:py-16">
            <div className="w-6 h-6 md:w-8 md:h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-3 md:mb-4"></div>
            <p className="text-gray-400 text-sm">{t('myOrders.loading')}</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 md:py-16 bg-zinc-900/50 border border-cyan-900/30 rounded-xl">
            <Package className="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto mb-3 md:mb-4" />
            <h3 className="text-lg md:text-xl font-bold text-white mb-2">{t('myOrders.noOrders')}</h3>
            <p className="text-gray-400 mb-4 md:mb-6 text-sm md:text-base">{t('myOrders.noOrdersDesc')}</p>
            <Button
              onClick={() => navigate('/services')}
              className="bg-cyan-600 hover:bg-cyan-700 text-sm md:text-base"
            >
              {t('myOrders.viewServices')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {orders.map((order) => (
              <div 
                key={order.id}
                className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-4 md:p-6 hover:border-cyan-500/50 transition-all duration-300"
              >
                <div className="flex flex-col gap-3 md:gap-4">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-cyan-600/20 flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 md:w-6 md:h-6 text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold text-sm md:text-base">{getServiceName(order.service_type)}</h3>
                      <p className="text-gray-500 text-[10px] md:text-sm truncate">
                        #{order.id.slice(0, 8)} • {new Date(order.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <span className="text-lg md:text-xl font-bold text-cyan-500">{order.total_price}€</span>
                  </div>

                  <OrderProgress status={order.status} t={t} />
                  
                  <div className="flex flex-wrap items-center gap-2 justify-end">
                    {getStatusBadge(order.status)}
                    <button
                      onClick={() => handleDownloadInvoice(order.id)}
                      disabled={downloadingId === order.id}
                      data-testid={`download-invoice-${order.id.slice(0, 8)}`}
                      className="inline-flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 bg-cyan-500/20 text-cyan-400 text-[10px] md:text-xs rounded-lg hover:bg-cyan-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {downloadingId === order.id ? (
                        <Loader2 className="w-2.5 h-2.5 md:w-3 md:h-3 animate-spin" />
                      ) : (
                        <Download className="w-2.5 h-2.5 md:w-3 md:h-3" />
                      )}
                      {t('myOrders.invoicePdf')}
                    </button>
                    {order.ticket_channel_id && (
                      <a
                        href="https://discord.gg/TXEa2x6mjZ"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 bg-indigo-500/20 text-indigo-400 text-[10px] md:text-xs rounded-lg hover:bg-indigo-500/30 transition-colors"
                      >
                        <ExternalLink className="w-2.5 h-2.5 md:w-3 md:h-3" />
                        {t('myOrders.viewTicket')}
                      </a>
                    )}
                  </div>

                  <AddOnSection order={order} API_URL={API_URL} token={token} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MesCommandesPage;
